import React, { useContext, useState, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import "../styles/Dashboard-new.css";
import { ProfileContext } from "../contexts/ProfileContext";
import { OrganizationContext } from "../contexts/OrganizationContext";
import { PrayerRequestContext } from "../contexts/PrayerRequestContext";
import { sendMonthlyBill, getLastMonthlyBill } from "../services/stripe-service";
import { AddOrganization } from "./add-organization";
import CreateAccount from "../components/create-account";
import PrayerRequestDialog from "../components/dialogs/PrayerRequestDialog";
import CallTeal from "../assets/images/CallTeal.png";
import CallOrange from "../assets/images/CallOrange.png";
import PropertyWhite from "../assets/images/PropertyWhite.png";
import PropertyOrange from "../assets/images/PropertyOrange.png";
import RunWhite from "../assets/images/RunWhite.png";
import RunOrange from "../assets/images/RunOrange.png";
import BookWhite from "../assets/images/BookWhite.png";
import BookOrange from "../assets/images/BookOrange.png";
import LocationWhite from "../assets/images/LocationWhite.png";
import LocationOrange from "../assets/images/LocationOrange.png";
import HeadphoneWhite from "../assets/images/HeadphoneWhite.png";
import HeadphoneOrange from "../assets/images/HeadphoneOrange.png";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../js/firebase";
import CreateAccountCustom from "./CreateAccountCustom";

const DashboardPage = () => {
  const [hovered, setHovered] = useState(false);
  const [hoverOrange, setHoverOrange] = useState(false);
  const [runHover, setRunHover] = useState(false);
  const [bookHover, setBookHover] = useState(false);
  const [locationHover, setLocationHover] = useState(false);
  const [headphoneHover, setHeadphoneHover] = useState(false);

  // Admin button states
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showPrayerDialog, setShowPrayerDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({
    requestedBy: "",
    email: "",
    source: "",
    category: "",
    prayer_request: "",
    notes: "",
    targetPrayerCount: 1,
    currentPrayerCount: 0,
  });

  // Dashboard data states
  const [summaryData, setSummaryData] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [isLoadingDetailed, setIsLoadingDetailed] = useState(true);
  const [lastBillMonth, setLastBillMonth] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [sinceOnboardingLabel, setSinceOnboardingLabel] =
    useState("Since Onboarding");

  const {
    worker,
    getOrgCallMetrics,
    getOrgCallMetricsDetailed,
    organizationNumber,
    organization,
    viewingAsOrganization,
    organizationFirebaseId, // Get Firebase ID from context
  } = useContext(ProfileContext);

  // Prefer document id on `organization` so metrics use the selected org immediately.
  // `organizationFirebaseId` is synced in ProfileContext in a later effect and lags after subscriber selection.
  const metricsFirebaseId = useMemo(
    () => organization?.id || organizationFirebaseId || "",
    [organization?.id, organizationFirebaseId],
  );

  // Add OrganizationContext for admin functions
  const { getTotalMonthlyCalls } = useContext(OrganizationContext);

  // Add PrayerRequestContext
  const { createPrayerRequest, fetchPrayerRequests } =
    useContext(PrayerRequestContext);

  // State to store current organization's Firebase data
  const [currentOrganizationData, setCurrentOrganizationData] = useState(null);
  const [isLoadingOrgData, setIsLoadingOrgData] = useState(false);
  // Global totals for A1P subscribers (admin view)
  const [globalTotals, setGlobalTotals] = useState({
    total_calls_all: 0,
    volunteers_all_time: 0,
  });
  const [isLoadingGlobalTotals, setIsLoadingGlobalTotals] = useState(false);

  // Toggle date picker
  const handleDateRangeClick = () => {
    setTempStartDate(startDate || "");
    setTempEndDate(endDate || "");
    setShowDatePicker(!showDatePicker);
  };

  // ✅ Fetch current organization's data directly from Firebase
  const fetchCurrentOrganizationData = async () => {
    if (!organization?.name) {
      return;
    }

    try {
      setIsLoadingOrgData(true);

      // Create query to fetch only the current organization
      const orgRef = collection(db, "organizations");
      const q = query(orgRef, where("name", "==", organization.name));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        querySnapshot.forEach((doc) => {
          const orgData = {
            id: doc.id,
            ...doc.data(),
          };
          setCurrentOrganizationData(orgData);
        });
      } else {
        setCurrentOrganizationData(null);
      }
    } catch (error) {
      setCurrentOrganizationData(null);
    } finally {
      setIsLoadingOrgData(false);
    }
  };

  // ✅ UPDATED: Fetch Summary Metrics with Firebase ID
  const fetchSummaryMetrics = async () => {
    try {
      setIsLoadingSummary(true);

      // Use Firebase ID instead of organization number
      const result = await getOrgCallMetrics(
        metricsFirebaseId,
        null,
        null,
        organization?.name,
      );

      setSummaryData(result || null);
    } catch (error) {
      console.error("❌ Error fetching summary metrics:", error);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // ✅ UPDATED: Fetch Detailed Metrics with Firebase ID
  const fetchDetailedMetrics = async (fromDate = null, toDate = null) => {
    try {
      setIsLoadingDetailed(true);

      // Use Firebase ID instead of organization number
      const result = await getOrgCallMetricsDetailed(
        metricsFirebaseId,
        fromDate,
        toDate,
        organization?.name,
      );

      console.log("✅ Detailed Metrics Response:", result);
      setDetailedData(result || null);
    } catch (error) {
      console.error("❌ Error fetching detailed metrics:", error);
    } finally {
      setIsLoadingDetailed(false);
    }
  };

  // ✅ Run when the effective org id changes (single run per subscriber selection)
  useEffect(() => {
    if (!metricsFirebaseId) {
      console.log("⏳ Waiting for Firebase ID...");
      return;
    }

    fetchSummaryMetrics();
    fetchDetailedMetrics();

    if (organization?.name) {
      fetchCurrentOrganizationData();
    }
    // Fetch global totals for admin
    fetchGlobalTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricsFirebaseId, organization?.name]);

  // Fetch global totals API for admin dashboard
  const fetchGlobalTotals = async () => {
    // Only admins need this global totals
    if (!worker || (worker.role !== "admin" && worker.role !== "super_admin" && worker.role !== "c_suite")) return;

    try {
      setIsLoadingGlobalTotals(true);

      let url = `${process.env.REACT_APP_API_URL}/admin/org_global_totals?firebaseId=${encodeURIComponent(
        organizationFirebaseId || "",
      )}`;

      // include role=admin when viewing as admin account
        url += `&role=${worker.role}`;

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(url, options);
      if (!response.ok) {
        console.error("Failed to fetch global totals", response.status);
        setIsLoadingGlobalTotals(false);
        return;
      }

      const result = await response.json();

      // API may wrap data in original.data like the example
      const data = result?.original?.data || result?.data || result || {};

      setGlobalTotals({
        total_calls_all: data.total_calls_all || 0,
        volunteers_all_time: data.volunteers_all_time || 0,
      });
    } catch (error) {
      console.error("Error fetching global totals:", error);
    } finally {
      setIsLoadingGlobalTotals(false);
    }
  };

  // Fetch last monthly bill month when admin (for Send Monthly Bill button state)
  useEffect(() => {
    if (worker?.role !== "admin") return;
    getLastMonthlyBill()
      .then((data) => setLastBillMonth(data?.last_run_month ?? null))
      .catch(() => setLastBillMonth(null));
  }, [worker?.role]);

  function formatDate(date) {
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  }

  // ✅ Apply new date range (only affects detailed metrics)
  const handleApply = () => {
    const newStartDate = tempStartDate || null;
    const newEndDate = tempEndDate || null;
    if (newStartDate && newEndDate) {
      setSinceOnboardingLabel(
        formatDate(new Date(newStartDate)) +
          " To " +
          formatDate(new Date(newEndDate)),
      );
    }
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setShowDatePicker(false);
    fetchDetailedMetrics(newStartDate, newEndDate);
  };

  // ✅ Cancel selection
  const handleCancel = () => {
    setTempStartDate(startDate || "");
    setTempEndDate(endDate || "");
    setShowDatePicker(false);
  };

  // ✅ Clear date filter
  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setTempStartDate("");
    setTempEndDate("");
    setShowDatePicker(false);
    fetchDetailedMetrics(null, null);
    setSinceOnboardingLabel("Since Onboarding");
  };

  // Format date range display
  const formatDateRangeDisplay = () => {
    if (!startDate && !endDate) {
      return "Select Date";
    }
    if (startDate && endDate) {
      return `${new Date(startDate).toLocaleDateString()} - ${new Date(
        endDate,
      ).toLocaleDateString()}`;
    }
    return "Select Date Range";
  };

  // Admin button handlers
  const handleSendBill = async () => {
    try {
      const result = await getTotalMonthlyCalls();
      if (!result || result.length === 0) {
        console.error("No monthly organizations found to bill");
        return;
      }
      const response = await sendMonthlyBill(result);
      if (response.success) {
        const data = await getLastMonthlyBill();
        setLastBillMonth(data?.last_run_month ?? null);
        console.log("Monthly bills sent successfully");
        alert("Monthly bills sent successfully!");
      } else {
        console.error("Failed to send monthly bills:", response.message);
        alert("Failed to send monthly bills: " + response.message);
      }
    } catch (error) {
      console.error("Error in handleSendBill:", error);
      alert("Error sending monthly bills: " + error.message);
    }
  };

  const getCurrentMonthKey = () => {
    return new Date().toISOString().slice(0, 7); // "YYYY-MM"
  };

  const hasAlreadySentThisMonth = () => {
    if (!lastBillMonth) return false;
    return lastBillMonth === getCurrentMonthKey();
  };

  const canSendMonthlyBill = () => {
    return !hasAlreadySentThisMonth();
  };

  const handlePrayerSubmit = async (e) => {
    e.preventDefault();
    try {
      createPrayerRequest(newRequest);
      setShowPrayerDialog(false);
      setNewRequest({
        requestedBy: "",
        email: "",
        source: "",
        category: "",
        prayer_request: "",
        notes: "",
        targetPrayerCount: 1,
        currentPrayerCount: 0,
      });
      // Refresh prayer requests immediately after creating a new one
      fetchPrayerRequests();
      alert("Prayer request created successfully!");
    } catch (error) {
      console.error("Error creating prayer request:", error);
      alert("Error creating prayer request: " + error.message);
    }
  };

  // Extract data from Summary API (First 3 boxes)
  const totalCalls = summaryData?.total_calls_ytd || 0;
  const totalCallers = summaryData?.total_calls_current_month || 0;
  const volunteersOnboarded = summaryData?.volunteers_all_time || 0;

  // ✅ Extract data from Detailed API (Last 3 boxes)
  const totalDurationHrs = Math.round(detailedData?.total_calls_received || 0);
  const avgResponseTime = detailedData?.total_callers || 0;
  const completedSurveys = Math.round(
    detailedData?.total_call_duration_hours || 0,
  );

  return (
    <div className="dashboard-wrapper">
      <div className="welcome-header">
        <div className="welcome-title">Welcome back, {worker?.fullName}!</div>
        <p className="dashboard-subtitle">
          Dashboard Overview
          {/* {organization?.name?.toLowerCase() === "crossroads" ? ( */}
          {worker?.role !== "subscriber" && worker?.role !== "demo_subscriber" && !viewingAsOrganization ? (
           <>
           <span style={{ color: "#00323B" }}> | </span>
           <span style={{ color: "#ff7234" }}>AnyonePray</span>
           </>
          ) : (
            <>
              <span style={{ color: "#00323B" }}> | </span>
              <span style={{ color: "#ff7234" }}>{organization?.name}</span>
            </>
          )}
        </p>
      </div>

      {/* Admin Buttons Section - Only show if worker is admin */}
      {worker && worker.role === "admin" && (
        <div className="admin-buttons-container">
          <div className="admin-buttons-grid">
            <button
              className="admin-button add-org-btn"
              onClick={() => setShowAddOrg(true)}
            >
              ADD A NEW ORGANIZATION
            </button>
            <button
              className="admin-button create-account-btn"
              onClick={() => setShowCreateAccount(true)}
            >
              CREATE NEW USER ACCOUNT
            </button>
            <button
              className="admin-button prayer-request-btn"
              onClick={() => setShowPrayerDialog(true)}
            >
              ADD PRAYER REQUEST
            </button>
            <button
              disabled={!canSendMonthlyBill()}
              className={`admin-button send-bill-btn ${
                !canSendMonthlyBill() ? "disabled" : ""
              }`}
              onClick={() => handleSendBill()}
            >
              Send Monthly Bill
            </button>
          </div>
        </div>
      )}

      {/* --- Filter by Date (Only affects last 3 boxes) --- */}
      <div className="date-filter-container">
        <button
          className={`filter-date-btn ${showDatePicker ? "active" : ""}`}
          onClick={handleDateRangeClick}
        >
          Filter by Date
        </button>
        {(startDate || endDate) && (
          <button className="clear-date-btn" onClick={handleClearDates}>
            Clear
          </button>
        )}

        {showDatePicker && (
          <div className="date-picker-box">
            <div className="date-input-group">
              <label>From</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </div>
            <div className="date-input-group">
              <label>To</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </div>

            <div className="date-picker-actions">
              <button onClick={handleApply} className="apply-btn">
                Apply
              </button>
              <button onClick={handleCancel} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ✅ FIRST 3 BOXES - Summary API (No date filter) */}
      <div className="stats-section-bottom">
        <div className="section-label-top">{sinceOnboardingLabel}</div>
        <div className="stats-grid-bottom">
          <div className="stat-card-new stat-teal-new">
            <div
              className="stat-content-new stat-teal-new"
              style={{ borderRadius: "8px" }}
            >
              {isLoadingDetailed ? (
                <>
                  <div className="skeleton skeleton-number"></div>
                  <div className="skeleton skeleton-label"></div>
                </>
              ) : (
                <>
                  <div className="stat-number-new">
                    {totalDurationHrs.toLocaleString()}
                  </div>
                  <p className="stat-label-bottom orange">
                    TOTAL CALLS HANDLED
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="stat-card-new stat-orange-new">
            <div
              className="stat-content-new stat-orange-new"
              style={{ borderRadius: "8px" }}
            >
              {isLoadingDetailed ? (
                <>
                  <div className="skeleton skeleton-number"></div>
                  <div className="skeleton skeleton-label"></div>
                </>
              ) : (
                <>
                  <div className="stat-number-new">
                    {avgResponseTime.toLocaleString()}
                  </div>
                  <p className="stat-label-bottom teal">UNIQUE CALLERS</p>
                </>
              )}
            </div>
          </div>

          <div className="stat-card-new stat-teal-new">
            <div
              className="stat-content-new stat-teal-new"
              style={{ borderRadius: "8px" }}
            >
              {isLoadingDetailed ? (
                <>
                  <div className="skeleton skeleton-number"></div>
                  <div className="skeleton skeleton-label"></div>
                </>
              ) : (
                <>
                  <div className="stat-number-new">
                    {completedSurveys.toLocaleString()}
                  </div>
                  <p className="stat-label-bottom orange">
                    TOTAL CALL DURATION (HRS)
                  </p>
                </>
              )}
            </div>
                  </div>

                  {(!viewingAsOrganization) && worker && (worker.role === "admin" || worker.role === "super_admin" || worker.role === "c_suite")  && (
            <>
              <div className="stat-card-new stat-orange-new">
                <div
                  className="stat-content-new stat-orange-new"
                  style={{ borderRadius: "8px" }}
                >
                  {isLoadingDetailed ? (
                    <>
                      <div className="skeleton skeleton-number"></div>
                      <div className="skeleton skeleton-label"></div>
                    </>
                  ) : (
                    <>
                      <div className="stat-number-new">
                        {isLoadingGlobalTotals ? (
                          <div className="skeleton skeleton-number"></div>
                        ) : (
                          (globalTotals.total_calls_all || 0).toLocaleString()
                        )}
                      </div>
                      <p className="stat-label-bottom teal">A1P Subscribers Calls Handled</p>
                    </>
                  )}
                </div>
              </div>

              <div className="stat-card-new stat-teal-new">
                <div
                  className="stat-content-new stat-teal-new"
                  style={{ borderRadius: "8px" }}
                >
                  {isLoadingDetailed ? (
                    <>
                      <div className="skeleton skeleton-number"></div>
                      <div className="skeleton skeleton-label"></div>
                    </>
                  ) : (
                    <>
                      <div className="stat-number-new">
                        {isLoadingGlobalTotals ? (
                          <div className="skeleton skeleton-number"></div>
                        ) : (
                          (globalTotals.volunteers_all_time || 0).toLocaleString()
                        )}
                      </div>
                      <p className="stat-label-bottom orange">A1P Subscribers Volunteers</p>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

        </div>
      </div>  
      
      {/* ✅ LAST 3 BOXES - Detailed API (With date filter) */}

      <div className="stats-grid-container">
        <div className="stats-grid-top">
          <div className="stat-card-new stat-orange-new stats-grid-top-card">
            <div className="stat-label-top">YEAR TO DATE</div>
            <div className="stat-content-new stat-orange-new">
              {isLoadingSummary ? (
                <>
                  <div className="skeleton skeleton-number"></div>
                  <div className="skeleton skeleton-label"></div>
                </>
              ) : (
                <>
                  <div className="stat-number-new">
                    {totalCalls.toLocaleString("en-US")}
                  </div>
                  <p className="stat-label-bottom teal">TOTAL CALLS HANDLED</p>
                </>
              )}
            </div>
          </div>

          <div className="stat-card-new stat-teal-new stats-grid-top-card">
            <div className="stat-label-top">CURRENT MONTH</div>
            <div className="stat-content-new stat-teal-new">
              {isLoadingSummary ? (
                <>
                  <div className="skeleton skeleton-number"></div>
                  <div className="skeleton skeleton-label"></div>
                </>
              ) : (
                <>
                  <div className="stat-number-new">
                    {totalCallers.toLocaleString("en-US")}
                  </div>
                  <p className="stat-label-bottom orange">
                    TOTAL CALLS HANDLED
                  </p>
                </>
              )}
            </div>
          </div>
          {/* {worker && worker.role !== "subscriber" && ( */}
            <div className="stat-card-new stat-orange-new stats-grid-top-card">
              <div className="stat-label-top">TOTAL</div>
              <div className="stat-content-new stat-orange-new">
                {isLoadingSummary ? (
                  <>
                    <div className="skeleton skeleton-number"></div>
                    <div className="skeleton skeleton-label"></div>
                  </>
                ) : (
                  <>
                    <div className="stat-number-new">
                      {volunteersOnboarded.toLocaleString("en-US")}
                    </div>
                    <p className="stat-label-bottom teal">
                      VOLUNTEERS ONBOARDED
                    </p>
                  </>
                )}
              </div>
            </div>
          {/* )} */}
        </div>
      </div>

    
      

      {/* --- Actions Section --- */}
      <div className="action-grid-upper">
        <NavLink
          to="/reports/types-of-calls"
          className="action-card teal"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="action-logo">
            <img src={hovered ? CallOrange : CallTeal} alt="call Logo" />
          </div>
          <div>TYPES OF CALLS</div>
        </NavLink>

        <NavLink
          to="/reports/disposition"
          className="action-card orange"
          onMouseEnter={() => setHoverOrange(true)}
          onMouseLeave={() => setHoverOrange(false)}
        >
          <div className="action-logo">
            <img
              src={hoverOrange ? PropertyOrange : PropertyWhite}
              alt="property logo"
            />
          </div>
          <div>
            DISPOSITION <br /> OF CALLS
          </div>
        </NavLink>

        <NavLink
          to="/reports/age-+-gender"
          className="action-card teal"
          onMouseEnter={() => setRunHover(true)}
          onMouseLeave={() => setRunHover(false)}
        >
          <div className="action-logo">
            <img
              src={runHover ? RunOrange : RunWhite}
              alt="Age + Gender Logo"
            />
          </div>
          <div>
            AGE + GENDER <br /> OF CALLERS
          </div>
        </NavLink>
      </div>

      <div className="action-grid-lower">
        <NavLink
          to="/reports/resource"
          className="action-card orange"
          onMouseEnter={() => setBookHover(true)}
          onMouseLeave={() => setBookHover(false)}
        >
          <div className="action-logo">
            <img
              src={bookHover ? BookOrange : BookWhite}
              alt="Resources Logo"
            />
          </div>
          <div>RESOURCES</div>
        </NavLink>

        <NavLink
          to="/reports/calls-by-area-codes"
          className="action-card teal"
          onMouseEnter={() => setLocationHover(true)}
          onMouseLeave={() => setLocationHover(false)}
        >
          <div className="action-logo">
            <img
              src={locationHover ? LocationOrange : LocationWhite}
              alt="Location Logo"
            />
          </div>
          <div>
            CALLS BY <br /> AREA CODES
          </div>
        </NavLink>
        {worker && worker.role !== "subscriber" && (
          <NavLink
            to="/reports/prayer-partner-hours"
            className="action-card orange"
            onMouseEnter={() => setHeadphoneHover(true)}
            onMouseLeave={() => setHeadphoneHover(false)}
          >
            <div className="action-logo">
              <img
                src={headphoneHover ? HeadphoneOrange : HeadphoneWhite}
                alt="Agent Work Hours Logo"
              />
            </div>
            <div>
              AGENT <br /> WORK HOURS
            </div>
          </NavLink>
        )}
      </div>

      {/* Admin Modals/Dialogs */}
      <AddOrganization
        setShow={setShowAddOrg}
        show={showAddOrg}
        onHide={() => setShowAddOrg(false)}
      />
      <CreateAccountCustom
        showLock={showCreateAccount}
        setShowLock={setShowCreateAccount}
      />
      {/* <CreateAccount
        showLock={showCreateAccount}
        setShowLock={setShowCreateAccount}
      /> */}
      <PrayerRequestDialog
        open={showPrayerDialog}
        onClose={() => setShowPrayerDialog(false)}
        title="New Prayer Request Submission"
        request={newRequest}
        onChange={setNewRequest}
        onSubmit={handlePrayerSubmit}
        isNew={true}
      />
    </div>
  );
};

export default DashboardPage;
